// This is a copy of the states of a DNDeployment as parsed through getStateForHumans()
export const STATE_NEW = 'New';
export const STATE_SUBMITTED = 'Submitted';
export const STATE_INVALID = 'Skipped';
export const STATE_APPROVED = 'Approved';
export const STATE_REJECTED = 'Rejected';
export const STATE_QUEUED = 'Queued';
export const STATE_DEPLOYING = 'Deploying';
export const STATE_ABORTING = 'Aborting';
export const STATE_COMPLETED = 'Completed';
export const STATE_FAILED = 'Failed';
export const STATE_DELETED = 'Deleted';

// These constants are a subset of the deploynaut states
// this is because an approval can be bypassed and also
// several deployment states maps to 'approved'.
export const APPROVAL_SUBMITTED = 'submitted';
export const APPROVAL_REJECTED = 'rejected';
export const APPROVAL_BYPASSED = 'bypassed';
export const APPROVAL_APPROVED = 'approved';

export const REF_TYPE_FROM_UAT = 'promote_from_uat';
export const REF_TYPE_BRANCH = 'branch';
export const REF_TYPE_TAG = 'tag';
export const REF_TYPE_SHA = 'sha';
export const REF_TYPE_PACKAGE = 'package';
export const REF_TYPE_PACKAGE_ARCHIVE = 'package_archive';
export const REF_TYPE_REDEPLOY_CURRENT = 'redeploy';

export function hasLogs(deployState) {
	if (deployState === STATE_NEW) {
		return false;
	}
	if (deployState === STATE_SUBMITTED) {
		return false;
	}
	if (deployState === STATE_INVALID) {
		return false;
	}
	if (deployState === STATE_APPROVED) {
		return false;
	}
	if (deployState === STATE_REJECTED) {
		return false;
	}
	return true;
}

export function isDeploying(deployState) {
	if (deployState === STATE_QUEUED) {
		return true;
	}
	if (deployState === STATE_DEPLOYING) {
		return true;
	}
	if (deployState === STATE_ABORTING) {
		return true;
	}
	return false;
}

export function isAborting(deployState) {
	return deployState === STATE_ABORTING;
}

export function canAbort(deployState) {
	if (deployState === STATE_DEPLOYING) {
		return true;
	}
	if (deployState === STATE_ABORTING) {
		return true;
	}
	return false;
}

export function isDeployDone(deployState) {
	if (deployState === STATE_COMPLETED) {
		return true;
	}
	if (deployState === STATE_FAILED) {
		return true;
	}
	return false;
}

export function isDeployFailed(deployState) {
	if (deployState === STATE_FAILED) {
		return true;
	}
	return false;
}

export function hasDeployStarted(deployState) {
	return isDeployDone(deployState) || isDeploying(deployState);
}

export function isSubmitted(deployState) {
	return deployState === STATE_SUBMITTED;
}

export function isApproved(deployState) {
	if (hasDeployStarted(deployState)) {
		return true;
	}
	if (deployState === STATE_APPROVED) {
		return true;
	}
	return false;
}

export function isRejected(deployState) {
	return deployState === STATE_REJECTED;
}

export function isQueued(deployState) {
	return deployState === STATE_QUEUED;
}

export function isDeleted(deployState) {
	return deployState === STATE_DELETED;
}

export function isInvalid(deployState) {
	return deployState === STATE_INVALID;
}

export function canDelete(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (hasDeployStarted(current.state)) {
		return false;
	}
	return true;
}

export function canEdit(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (isSubmitted(current.state)) {
		return false;
	}
	if (isApproved(current.state)) {
		return false;
	}
	if (isRejected(current.state)) {
		return false;
	}
	if (isQueued(current.state)) {
		return false;
	}
	if (isInvalid(current.state)) {
		return false;
	}
	return true;
}

export function canApprove(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (!state.user.can_approve) {
		return false;
	}
	if (current.deployer && (current.deployer.id === state.user.id)) {
		return false;
	}
	if (!isSubmitted(current.state)) {
		return false;
	}
	return true;
}

export function canBypass(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (!state.user.can_bypass_approval) {
		return false;
	}
	if (isApproved(current.state)) {
		return false;
	}
	if (isRejected(current.state)) {
		return false;
	}

	// Submitted state does not allow bypass, except for the case where the requester
	// has permissions to bypass their own submission if they assigned it to someone else
	// and wish to bypass anyway. This is because they will not be shown approve or reject
	// actions on their own submissions.
	if (isSubmitted(current.state) && current.deployer && (current.deployer.id !== state.user.id)) {
		return false;
	}
	return true;
}

export function canReject(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (!state.user.can_approve) {
		return false;
	}
	if (current.deployer && (current.deployer.id === state.user.id)) {
		return false;
	}
	if (isApproved(current.state)) {
		return false;
	}
	if (isRejected(current.state)) {
		return false;
	}
	return true;
}

export function canDeploy(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (!canUnlock(state)) {
		return false;
	}
	if (hasDeployStarted(current.state)) {
		return false;
	}
	if (isApproved(current.state)) {
		return true;
	}

	return false;
}

export function canUnlock(state) {
	const current = state.deployment.list[state.deployment.current_id] || {};
	if (state.user.is_admin) {
		return true;
	}
	if (!current.locked_by) {
		return true;
	}
	if (current.locked_by.id === state.user.id) {
		return true;
	}

	return false;
}

// this function maps deployment state to an approval state
export function getApprovalState(deployState, approverName) {
	switch (deployState) {
		case STATE_SUBMITTED:
			return APPROVAL_SUBMITTED;
		case STATE_REJECTED:
			return APPROVAL_REJECTED;
		case STATE_APPROVED:
		case STATE_QUEUED:
		case STATE_DEPLOYING:
		case STATE_INVALID:
		case STATE_FAILED:
		case STATE_COMPLETED:
			if (!approverName) {
				return APPROVAL_BYPASSED;
			}
			return APPROVAL_APPROVED;
		default:
			return '';
	}
}
